import React, {useEffect, useRef} from 'react';
import {useVirtualizer} from '@tanstack/react-virtual';
import {faEllipsisH, faPlus, faTrash} from "@fortawesome/free-solid-svg-icons";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {millisecondsToTimestamp, timestampToMilliseconds} from "@/utils/subtitleUtils";
import ClientRender from "@/Components/ClientOnly";
import Dropdown from "@/Components/Dropdown";
import {Subtitle} from "@/models/Subtitle";
import {SearchPattern} from "@/Pages/Subtitle/Partials/SearchDropdown";

interface VirtualizedListProps {
    items: Subtitle[];
    currentItemId: string | null;
    onSetCurrentItemIdx: (index: number) => void;
    updateItem: (index: number, value: Subtitle) => void;
    deleteItem: (index: number) => void;
    addItem: (prev: Subtitle, next?: Subtitle) => void;
    className?: string;
    shouldAutoScroll?: boolean;
    isLoading?: boolean;
    searchPattern?: SearchPattern;
}

type TimeField = 'startTime' | 'endTime';

const invalidFieldClass = ['!bg-red-100', 'dark:!bg-red-500/30']

const SubtitleList: React.FC<VirtualizedListProps> = ({
    items,
    currentItemId,
    updateItem,
    deleteItem,
    addItem,
    className,
    onSetCurrentItemIdx,
    shouldAutoScroll = true,
    isLoading = false,
    searchPattern
}) => {
    const [filteredItems, setFilteredItems] = React.useState<Subtitle[]>([]);
    const [focusedItemId, setFocusedItemId] = React.useState<string | null>(null);
    const parentRef = useRef<HTMLDivElement>(null);
    const isUserEditingRef = useRef(false);
    const contentEditableRefs = useRef<Map<string, HTMLDivElement>>(new Map());
    const focusedItemIdRef = useRef<string | null>(null);

    const getHighlightedHtml = React.useCallback((text: string): string | null => {
        if (!searchPattern) return null;
        try {
            const flags = searchPattern.caseSensitive ? 'g' : 'gi';
            const regex = new RegExp(`(${searchPattern.pattern})`, flags);
            if (!regex.test(text)) return null;
            const escaped = text
                .replace(/&/g, '&amp;')
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;');
            return escaped.replace(
                new RegExp(`(${searchPattern.pattern})`, flags),
                '<mark class="bg-yellow-200 dark:bg-yellow-500/80 rounded-sm">$1</mark>'
            );
        } catch {
            return null;
        }
    }, [searchPattern]);

    useEffect(() => {
        contentEditableRefs.current.forEach((div, id) => {
            const item = filteredItems.find(i => i.id === id);
            if (!item) return;
            const isFocused = document.activeElement === div || focusedItemIdRef.current === id;
            if (!isFocused) {
                const highlighted = getHighlightedHtml(item.text);
                if (highlighted) {
                    div.innerHTML = highlighted;
                } else {
                    div.innerText = item.text;
                }
            }
        });
    }, [searchPattern]);

    useEffect(() => {
        isUserEditingRef.current = false;
    });

    useEffect(() => {
        if(isLoading) {
            setFilteredItems([])
        }
    }, [isLoading]);

    useEffect(() => {
        const newItems = items.filter(el => !el.deleted);
        newItems.sort((a, b) => a.startTimeMs - b.startTimeMs);
        setFilteredItems(newItems);
    }, [items]);

    const getItemKey = React.useCallback(
        (index: number) => filteredItems[index]?.id ?? index,
        [filteredItems]
    );

    const virtualizer = useVirtualizer({
        count: filteredItems.length,
        getScrollElement: () => parentRef.current,
        estimateSize: () => 66,
        overscan: 50,
        getItemKey,
        measureElement: (element) => {
            return element.getBoundingClientRect().height;
        }
    });

    const scrollIndex = React.useMemo(() => {
        if (currentItemId === null) return null;
        const index = filteredItems.findIndex(item => item.id === currentItemId);
        return index >= 0 ? index : null;
    }, [currentItemId, filteredItems]);

    const prevScrollIndexRef = useRef<number | null>(null);

    useEffect(() => {
        if (shouldAutoScroll && !isUserEditingRef.current && scrollIndex !== null) {
            const prevIndex = prevScrollIndexRef.current;
            const jumpDistance = prevIndex !== null ? Math.abs(scrollIndex - prevIndex) : 0;
            const useSmooth = jumpDistance > 0 && jumpDistance <= 50;

            virtualizer.scrollToIndex(scrollIndex, {
                align: 'auto',
                behavior: useSmooth ? 'smooth' : 'auto'
            });
            prevScrollIndexRef.current = scrollIndex;
        }
    }, [scrollIndex, shouldAutoScroll, virtualizer]);

    const handleContentChange = (e: React.FormEvent<HTMLDivElement>, index: number) => {
        isUserEditingRef.current = true;
        const text = e.currentTarget.innerText || '';
        const updatedItem = new Subtitle({...filteredItems[index], text});
        updateItem(getOriginalIndex(index), updatedItem);
    };

    const validateTimestamp = (value: string, field: TimeField, idx: number): boolean => {
        const regex = /^([0-9]{2}):([0-9]{2}):([0-9]{2}),([0-9]{3})$/;
        const match = value.match(regex);
        if (!match) {
            return false;
        }
        const valueMs = timestampToMilliseconds(value);
        const counterPartField = field === 'startTime' ? 'endTimeMs' : 'startTimeMs';
        const counterPartValue = filteredItems[idx][counterPartField]
        if (field === 'startTime' && valueMs >= counterPartValue) {
            return false;
        }
        if (field === 'endTime' && valueMs <= counterPartValue) {
            return false;
        }
        if (field === 'startTime') {
            const previous = idx > 0 ? filteredItems[idx - 1] : null;
            if (previous) {
                if (valueMs < previous.endTimeMs) {
                    return false;
                }
            }
        } else if (field === 'endTime') {
            const next = idx < filteredItems.length - 1 ? filteredItems[idx + 1] : null;
            if (next) {
                if (valueMs > next.startTimeMs) {
                    return false;
                }
            }
        }
        return true;
    }

    const handleTimeChange = (e: React.ChangeEvent<HTMLInputElement>, index: number, field: TimeField) => {
        e.target.classList.remove(...invalidFieldClass);
        if (!validateTimestamp(e.target.value, field, index)) {
            e.target.classList.add(...invalidFieldClass);
        }
    }

    const publishTimeChange = (e: React.ChangeEvent<HTMLInputElement>, index: number, field: TimeField) => {
        const value = e.target.value;
        const originField = field === 'startTime' ? 'startTimeMs' : 'endTimeMs';
        if(!validateTimestamp(value, field, index)) {
            e.target.value = millisecondsToTimestamp(filteredItems[index][originField]);
        } else {
            const valueMs = timestampToMilliseconds(value);
            if (valueMs !== filteredItems[index][originField]) {
                const updatedItem = new Subtitle({...filteredItems[index], [field]: valueMs});
                if (field === 'startTime') {
                    updatedItem.startTimeMs = valueMs;
                } else {
                    updatedItem.endTimeMs = valueMs;
                }
                updateItem(getOriginalIndex(index), updatedItem);
            }
        }
        e.target.classList.remove(...invalidFieldClass);
    }

    const getOriginalIndex = (index: number) => {
        if (!filteredItems[index]) return -1;
        return items.findIndex(el => el.id === filteredItems[index].id);
    }

    if (isLoading) {
        return (
            <ClientRender>
                <div className={className} style={{overflow: 'auto'}}>
                    <div className="animate-pulse">
                        {Array.from({length: 10}).map((_, idx) => (
                            <SkeletonSubtitleRow key={idx} />
                        ))}
                    </div>
                </div>
            </ClientRender>
        );
    }

    return (
        <ClientRender>
            <div
                ref={parentRef}
                className={className}
                style={{
                    overflow: 'auto',
                    contain: 'strict'
                }}
            >
                <div
                    style={{
                        height: `${virtualizer.getTotalSize()}px`,
                        width: '100%',
                        position: 'relative'
                    }}
                >
                        {virtualizer.getVirtualItems().map((virtualRow) => {
                            const item = filteredItems[virtualRow.index];
                            if (!item) return null;

                            return (
                                <div
                                    key={virtualRow.key}
                                    data-index={virtualRow.index}
                                    ref={virtualizer.measureElement}
                                    onClick={() => onSetCurrentItemIdx(getOriginalIndex(virtualRow.index))}
                                    style={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        width: '100%',
                                        transform: `translateY(${virtualRow.start}px)`
                                    }}
                                    className="subtitle-item pr-2 border-b border-gray-200 dark:border-gray-700 border-solid relative group flex items-stretch justify-between"
                                >
                                    <div
                                        className="border-r border-gray-200 dark:border-gray-700 pr-1 w-32 flex-shrink-0 flex justify-center items-center py-2 relative">
                                        <div className="flex items-start gap-2">
                                            <span className="text-gray-400 dark:text-gray-500 text-sm -mt-0.5 ml-2">{virtualRow.index + 1}</span>
                                            <div className="flex flex-col gap-1">
                                                <input
                                                    key={item.startTimeMs}
                                                    onBlur={(e) => publishTimeChange(e, virtualRow.index, 'startTime')}
                                                    onChange={(e) => handleTimeChange(e, virtualRow.index, 'startTime')}
                                                    type="text" defaultValue={millisecondsToTimestamp(item.startTimeMs)}
                                                    className="text-xs hover:bg-amber-50 dark:hover:bg-amber-900/30 cursor-pointer p-0 border-0 bg-transparent text-gray-900 dark:text-gray-200 w-[10.5ch] rounded-sm"/>
                                                <input type="text" defaultValue={millisecondsToTimestamp(item.endTimeMs)}
                                                       key={item.endTimeMs}
                                                       onBlur={(e) => publishTimeChange(e, virtualRow.index, 'endTime')}
                                                       onChange={(e) => handleTimeChange(e, virtualRow.index, 'endTime')}
                                                       className="text-xs hover:bg-amber-50 dark:hover:bg-amber-900/30 cursor-pointer p-0 border-0 bg-transparent text-gray-900 dark:text-gray-200 w-[10.5ch] rounded-sm"/>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="grow relative flex items-center">
                                        <div
                                            dir="auto"
                                            id={item.id}
                                            contentEditable
                                            suppressContentEditableWarning
                                            className={`px-4 pr-7 py-1 text-base block w-full notranslate border-0 bg-transparent focus:outline-none whitespace-pre-wrap break-words ${item.id === currentItemId ? 'text-blue-700 dark:text-blue-400' : 'text-gray-900 dark:text-gray-200'}`}
                                            onInput={(e) => handleContentChange(e, virtualRow.index)}
                                            onFocus={(e) => {
                                                focusedItemIdRef.current = item.id;
                                                setFocusedItemId(item.id);
                                                if (searchPattern && e.currentTarget.innerHTML !== item.text) {
                                                    e.currentTarget.innerText = item.text;
                                                }
                                            }}
                                            onBlur={() => {
                                                if (focusedItemIdRef.current === item.id) {
                                                    focusedItemIdRef.current = null;
                                                }
                                                setFocusedItemId(null);
                                            }}
                                            ref={(div) => {
                                                if (div) {
                                                    contentEditableRefs.current.set(item.id, div);
                                                    const isFocused = document.activeElement === div || focusedItemIdRef.current === item.id;
                                                    if (!isFocused) {
                                                        const highlighted = getHighlightedHtml(item.text);
                                                        if (highlighted) {
                                                            div.innerHTML = highlighted;
                                                        } else if (div.innerText !== item.text) {
                                                            div.innerText = item.text;
                                                        }
                                                    }
                                                } else {
                                                    contentEditableRefs.current.delete(item.id);
                                                }
                                            }}
                                        />
                                        <div className="absolute top-1/2 -translate-y-1/2 -right-0.5">
                                            <Dropdown>
                                                <Dropdown.Trigger>
                                                    {(isOpen) => (
                                                        <button
                                                            className={`w-6 h-6 flex items-center justify-center text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition-all duration-200 ${isOpen ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}>
                                                            <FontAwesomeIcon className="w-3 h-3" icon={faEllipsisH}/>
                                                        </button>
                                                    )}
                                                </Dropdown.Trigger>
                                                <Dropdown.Content align="right">
                                                    <Dropdown.Item
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            addItem(item, filteredItems[virtualRow.index + 1]);
                                                        }}
                                                    >
                                                        <FontAwesomeIcon className="w-3 h-3 mr-2" icon={faPlus}/>
                                                        Add Below
                                                    </Dropdown.Item>
                                                    <Dropdown.Item
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            deleteItem(getOriginalIndex(virtualRow.index));
                                                        }}
                                                    >
                                                        <FontAwesomeIcon className="w-3 h-3 mr-2" icon={faTrash}/>
                                                        Delete
                                                    </Dropdown.Item>
                                                </Dropdown.Content>
                                            </Dropdown>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                </div>
            </div>
        </ClientRender>
    );
};

function SkeletonSubtitleRow() {
    const widths = ['w-[60%]', 'w-[75%]', 'w-[50%]', 'w-[85%]'];
    const [width, setWidth] = React.useState('');

    useEffect(() => {
        const randomWidth = widths[Math.floor(Math.random() * widths.length)];
        setWidth(randomWidth);
    }, []);

    return (
        <div className="subtitle-item pr-2 border-b border-gray-200 dark:border-gray-700 border-solid flex items-center justify-between py-2 px-2">
            <div className="h-full w-32 flex-shrink-0 flex justify-center items-start pt-2 pb-2">
                <div className="flex items-start gap-2">
                    <div className="h-3 w-4 bg-gray-200 dark:bg-gray-700 rounded ml-2"></div>
                    <div className="flex flex-col gap-1">
                        <div className="h-3 w-[10.5ch] bg-gray-200 dark:bg-gray-700 rounded"></div>
                        <div className="h-3 w-[10.5ch] bg-gray-200 dark:bg-gray-700 rounded"></div>
                    </div>
                </div>
            </div>
            <div className="grow px-4">
                <div className={`h-3 bg-gray-200 dark:bg-gray-700 rounded ${width}`}></div>
            </div>
        </div>
    );
}

export default SubtitleList;
